import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { MessageCircle, Send, Bot, User, Loader2, Sparkles } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatbotPage = () => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Waheguru Ji Ka Khalsa, Waheguru Ji Ki Fateh! 🙏\n\nI am your AI assistant for learning about Sikhism. You can ask me about:\n\n• Gurbani and its meanings\n• The lives of the ten Gurus\n• Sikh history and traditions\n• Gurudwara etiquette\n• Punjabi and Gurmukhi basics\n\nHow can I help you today?'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const suggestedQuestions = [
    'What is the significance of Japji Sahib?',
    'Tell me about Guru Nanak Dev Ji',
    'What are the 5 Ks in Sikhism?',
    'Explain the concept of Langar',
  ];

  const handleSend = async (messageText = input) => {
    if (!messageText.trim() || loading) return;

    const userMessage = { role: 'user', content: messageText };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/chat`, {
        message: messageText,
        session_id: sessionId
      });
      
      setSessionId(response.data.session_id);
      
      const assistantMessage = {
        role: 'assistant',
        content: response.data.response
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'I apologize, but I encountered an error. Please try again.'
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="min-h-screen bg-cream-100 py-8" data-testid="chatbot-page">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-royal-100 px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-5 h-5 text-royal-600" />
            <span className="text-royal-700 font-medium">AI-Powered Learning</span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-royal-700 mb-2">
            Sikh Learning Assistant
          </h1>
          <p className="text-gray-600">
            Ask questions about Sikhism, Gurbani, and Sikh history
          </p>
        </div>

        {/* Chat Container */}
        <Card className="shadow-xl border-saffron-100">
          <CardContent className="p-0">
            {/* Messages Area */}
            <ScrollArea className="h-[500px] p-6">
              <div className="space-y-6">
                {messages.map((message, index) => (
                  <div
                    key={index}
                    className={`flex gap-3 ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
                    data-testid={`message-${index}`}
                  >
                    {/* Avatar */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
                      message.role === 'user' 
                        ? 'bg-royal-600 text-white' 
                        : 'bg-saffron-100 text-saffron-600'
                    }`}>
                      {message.role === 'user' ? (
                        <User className="w-5 h-5" />
                      ) : (
                        <Bot className="w-5 h-5" />
                      )}
                    </div>
                    
                    {/* Message Bubble */}
                    <div className={`max-w-[80%] ${
                      message.role === 'user' ? 'chat-user' : 'chat-ai'
                    } p-4`}>
                      <p className="whitespace-pre-wrap text-sm leading-relaxed">
                        {message.content}
                      </p>
                    </div>
                  </div>
                ))}
                
                {/* Loading indicator */}
                {loading && (
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-saffron-100 flex items-center justify-center">
                      <Bot className="w-5 h-5 text-saffron-600" />
                    </div>
                    <div className="chat-ai p-4">
                      <div className="flex items-center gap-2 text-gray-500">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span className="text-sm">Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Suggested Questions */}
            {messages.length <= 1 && (
              <div className="px-6 pb-4">
                <p className="text-xs text-gray-500 mb-2 uppercase tracking-wide">Suggested Questions</p>
                <div className="flex flex-wrap gap-2">
                  {suggestedQuestions.map((question, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      size="sm"
                      className="text-xs border-saffron-200 text-saffron-700 hover:bg-saffron-50"
                      onClick={() => handleSend(question)}
                      data-testid={`suggested-${index}`}
                    >
                      {question}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {/* Input Area */}
            <div className="border-t border-gray-100 p-4">
              <div className="flex gap-3">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about Sikhism, Gurbani, or Sikh history..."
                  className="flex-1 rounded-full border-saffron-200 focus:border-saffron-500"
                  disabled={loading}
                  data-testid="chat-input"
                />
                <Button
                  onClick={() => handleSend()}
                  disabled={!input.trim() || loading}
                  className="btn-primary rounded-full w-12 h-12 p-0"
                  data-testid="send-button"
                >
                  <Send className="w-5 h-5" />
                </Button>
              </div>
              <p className="text-xs text-gray-400 text-center mt-3">
                Powered by AI • For educational purposes only
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ChatbotPage;
